import { DatePipe } from "@angular/common";
import { Component, Input, OnInit, ViewChild } from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { ChartConfiguration } from "chart.js";
import moment from "moment";
import { BaseChartDirective } from "ng2-charts";
import { NavigationService } from "src/app/navigation/navigation.service";
import { CvConfigurationService } from "src/app/reusables/services/cv-configuration.service";
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { ModuleThemesService } from "src/app/reusables/services/module-themes.service";
import { ModuleManager } from "src/shared/models/module-manager";
import { ModuleManagerMenuItemType, ModuleManagerSubMenu } from 'src/shared/models/module-manager/module-manager-menu-item.model';
import { ModuleManagerRefreshType } from "src/shared/models/module-manager/module-manager-options.model";
import { ModuleManagerMetricInfo } from "src/shared/models/module-manager/module-manager-settings.models";
import { TrendingService } from "./trending.service";

@Component({
    selector: "cv-trending",
    templateUrl: "./trending.component.html",
    styleUrls: ["./trending.component.scss"],
})
export class TrendingComponent implements OnInit {
    @Input() moduleManager: ModuleManager;
    @Input() moduleKey: string;
    @Input() metrics: ModuleManagerMetricInfo[] = [];
    @ViewChild(BaseChartDirective) chartObject: BaseChartDirective;

    public noData = false; // Boolean indicating the data load was empty.
    public noMetric = false; // Boolean indicating that no metrics were selected.
    public isFirstLoad = true; // Used to log whether the data load is the first load since the module loaded.
    private loadingData = false; // Prevent non-force refreshes if another refresh is already happening. Also used to disable click on the "Refresh" action in the module menu.
    private dataLoadSuccess = false;
    private data = null;
    private cancelRefresh: () => void; // This function is used to resolve the cancellerRefresh promise in loadData()
    isCXOne = false;
    modalRef = null;
    hasObjective = false;

    //Chart.js Stuff
    public chartConfig = null;
    themeMenuOptions = [];
    objectiveLines = [];
    objectiveBoxes = [];

    //Chart module state
    chartState = {
        //State object properties
        legendPosition: "top",
        themeIndex: 0,
        name: "chartState",
        autoLoad: true,
        showObjective: false
    };
    chartLegendPosition =
        this.chartState.legendPosition != null
            ? this.chartState.legendPosition
            : "top";
    chartPadding = {
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
    };
    chartAxisShowStates = {};
    chartMaximumRotation = 90; //90 degrees is the default.
    firstAnimateComplete = false;
    showObjective = false;

    backgroundcolor = {
        id: 'custom_canvas_background_color',
        beforeDraw: (chart) => {
            const ctx = chart.canvas.getContext('2d');
            ctx.save();
            ctx.globalCompositeOperation = 'destination-over';
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, chart.canvas.width, chart.canvas.height);
            ctx.restore();
        }
    };

    //Color theme handling
    currentThemeIndex =
        this.chartState.themeIndex != null ? this.chartState.themeIndex : 0;
    currentTheme = null;

    constructor(
        private service: TrendingService,
        private moduleThemesService: ModuleThemesService,
        public config: CvConfigurationService,
        private datePipe: DatePipe,
        private cvLocalizationService: CVLocalizationService,
        private navigationService: NavigationService,
        private ngbModal: NgbModal,
    ) {
        this.isCXOne = config.isCxOne;
    }

    async ngOnInit() {
        this.moduleManager.showLoader = true;
        await this.cvLocalizationService.GetSection("Trending");
        this.moduleManager.setOptions({
            canRefresh: () => !this.loadingData,
            hasRefresh: true,
            onExport: (format, isCXOne) => this.service.exportData(this.moduleKey,
                format, isCXOne, this.moduleManager.container.moduleInfo.DTO.ModuleID),
            onImageExport: (isCXOne) => this.exportPNG(),
            onRefresh: (isAutoRefresh) => this.loadData(isAutoRefresh),
            imageExportVisible: () => true,
            refreshType: ModuleManagerRefreshType.Auto,
            onSettingsApplied: () => this.loadData(false, true), // This is called when the date range selector dropdown in the top-left is changed.  Force a refresh, in case the user is rapidly changing between date ranges.
        });

        this.moduleManager.addMenuItem({
            name: "Legend_Position",
            icon: "legend",
            type: ModuleManagerMenuItemType.SubMenu,
            getEnabled: () => this.chartObject != null,
            subItems: [{
                name: this.cvLocalizationService.getCachedText("Trending", "Top", "Top"),
                icon: "positionTop",
                click: () => this.setLegendPosition("top"),
            },
            {
                name: this.cvLocalizationService.getCachedText("Trending", "Bottom", "Bottom"),
                icon: "positionBottom",
                click: () => this.setLegendPosition("bottom"),
            },
            {
                name: this.cvLocalizationService.getCachedText("Trending", "Left", "Left"),
                icon: "positionLeft",
                click: () => this.setLegendPosition("left"),
            },
            {
                name: this.cvLocalizationService.getCachedText("Trending", "Right", "Right"),
                icon: "positionLeft",
                click: () => this.setLegendPosition("right"),
            }]
        } as ModuleManagerSubMenu);

        this.moduleManager.addState({
            name: 'chartState',
            autoLoad: true,
            getStateData: () => this.chartState,
            onStateLoaded: stateData => {
                if (!stateData) {
                    return;
                }
                this.chartState = stateData;
                this.currentThemeIndex = (this.chartState.themeIndex != null) ? this.chartState.themeIndex : 0;
                this.currentTheme = this.moduleThemesService.ModuleThemes[this.currentThemeIndex].Colors;
                this.setLegendPosition(this.chartState.legendPosition);
                this.showObjective = this.chartState.showObjective;
            }
        });
    }

    /**
     * Load Trending data and update the module state.
     */
    async loadData(
        isAutoRefresh: boolean,
        cancelAndForceRefresh: boolean = false
    ) {
        if (this.loadingData && !cancelAndForceRefresh) {
            return;
        }
        this.loadingData = true;

        // If we didn't return early, cancel the previous data load.
        if (this.cancelRefresh) {
            this.cancelRefresh();
        }
        const cancellerPromise: Promise<void> = new Promise(
            (resolve) => (this.cancelRefresh = () => resolve())
        );

        if (!isAutoRefresh) {
            this.moduleManager.showLoader = true;
        }

        try {
            const data = await this.service.loadData(this.moduleKey, isAutoRefresh, this.isFirstLoad, cancellerPromise);
            this.dataLoadSuccess = true;
            this.cancelRefresh = null;
            this.loadingData = false;
            this.isFirstLoad = false;
            this.noMetric = this.moduleManager.container.noMetricsSelected();
            this.data = data;
            this.noData = !this.data?.GraphItem;
            this.createChart();
            this.moduleManager.container.showDateRange = true;
            this.moduleManager.showLoader = false;
            this.moduleManager.setAutoRefreshReady();
            this.updateModuleNameSuffix();
        } catch (e) {
            // If the exception is *not* a canceled refresh, have moduleManager handle the error.
            // A canceled refresh isn't something to be concerned about, since another refresh is happening.
            if (e?.code === DOMException.ABORT_ERR || e?.statusText === 'abort') {
                return;
            }
            console.error("Exception in Trending loadData", e);
            this.moduleManager.handleLoadError(e);
            this.loadingData = false;
        }
    }

    createChart() {
        // Check for null/empty data sets
        // If found, create an empty chart.
        if (
            this.data == null ||
            this.noData ||
            this.data.GraphItem.length === 0
        ) {
            this.chartConfig = {
                type: "line",
                data: {
                    labels: ["0"],
                    datasets: [
                        {
                            label: "",
                            backgroundColor: "rgba(0,0,0,0)",
                            borderColor: "rgba(0,0,0,0)",
                            data: [0],
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    animation: {
                        duration: 0,
                    },
                    scaleShowGridLines: true,
                    scaleShowVerticalLines: true,
                    showTooltips: false,
                    scales: {
                        y: {
                            ticks: {
                                beginAtZero: true,
                            },
                        },
                    },
                },
                plugins: [this.backgroundcolor]
            } as ChartConfiguration;
            return;
        }

        const preppedDatasets = [];
        const seriesTypes = [];
        let xAxisLabels = [];
        const preparedYAxes = {};
        const xAxisId = "x";

        //Char Data
        const element = this.data.GraphItem;

        // Create Y-Axes
        const yAxisId = "YAxisType" + element.NumericType;

        // Check if a Y-Axis for this NumericType already exists, creating a new one if not.
        if (seriesTypes.indexOf(element.NumericType) < 0) {
            this.chartAxisShowStates[yAxisId] = 1;
            seriesTypes.push(element.NumericType);

            //Check for Type 3 (Time) metrics. The scale requires special axis-tick formatting. If detected, push an axis with the right configuration. Othewise, use the default.
            if (element.NumericType === 3) {
                Object.assign(preparedYAxes, {
                    [yAxisId]: {
                        id: yAxisId,
                        display: true, // reset in verifyYAxisDisplay()
                        position: seriesTypes.length <= 2 ? "left" : "right", //4 total types (enums: 0,1,2,3). Put the third and fourth types' axes on the right.
                        title: {
                            display: true,
                            font: {
                                weight: "bold",
                            },
                            text: element.NumericTypeName,
                        },
                        ticks: {
                            //"2018-01-01" is arbitrary. We just need a valid date object to allow us to convert a total-seconds value to HH:MM:SS formatting.
                            callback: (value) =>
                                moment("2018-01-01")
                                    .startOf("day")
                                    .seconds(value)
                                    .format("HH:mm:ss")
                                    .toString(),
                        },
                    },
                });
            } else {
                //Default axis config
                Object.assign(preparedYAxes, {
                    [yAxisId]: {
                        id: yAxisId,
                        display: true, // reset in verifyYAxisDisplay()
                        position: seriesTypes.length <= 2 ? "left" : "right", //4 total types (enums: 0,1,2,3). Put the third and fourth types' axes on the right
                        title: {
                            display: true,
                            font: {
                                weight: "bold",
                            },
                            text: element.NumericTypeName,
                        },
                    },
                });
            }
        } else {
            this.chartAxisShowStates[yAxisId] += 1;
        }

        //Ensure a theme exists
        if (this.config.isCxOne) {
            this.currentTheme =
                this.moduleThemesService.ModuleThemes[12].Colors;
        }
        else if (this.currentTheme == null) {
            this.currentTheme = this.moduleThemesService.ModuleThemes[0].Colors;
        }


        //Add the series to be displayed
        preppedDatasets.push({
            xAxisID: xAxisId,
            yAxisID: yAxisId,
            label: element.MetricCustomName ? `${element.MetricCustomName}*` : element.MetricName,
            backgroundColor: 'rgba(36,79,84,0.7)',
            borderColor: 'rgba(36,79,84,0.7)',
            data: element.MetricValues,
            displayValues: element.MetricDisplayValues,
            dataType: element.NumericType, //Based on framework type: ClearView.Quality.Metrics.Enums.NumericType
            fill: false,
            hitRadius: this.config.isCxOne ? 1 : 12,
            hoverRadius: 6,
            pointRadius: 2,
            pointBorderColor: 'rgba(36,79,84,0.7)',
            pointBackgroundColor: 'rgba(36,79,84,0.7)',
        });

        // Prep the x-axis labels
        // The server will fill values for all necessary labels.
        xAxisLabels = this.data.GraphItem.IntervalGroups.slice(); // copy of the original

        // If it's a date type (i.e. "9/4/18"), get rid of the year from the label.
        // Types checked for are based off of the TimeInterval enum assigned in the controller.
        if ([2, 4, 5].indexOf(this.data.GraphItem.IntervalType) >= 0) {
            // If IntervalType is 2, 4, or 5
            for (let i = 0; i < xAxisLabels.length; i++) {
                xAxisLabels[i] = moment(
                    xAxisLabels[i],
                    CVMomentDateFormat
                ).format(CVMomentMonthDayFormat);
            }
        }

        //Check for x-axis date types to keep flat
        if ([2, 3, 4, 5].indexOf(this.data.GraphItem.IntervalType) >= 0) {
            // If IntervalType is 2, 3, 4, or 5
            this.chartMaximumRotation = 0;
        }

        if (this.chartLegendPosition == null) {
            this.chartLegendPosition =
                this.chartState.legendPosition != null
                    ? this.chartState.legendPosition
                    : "top";
        }
        this.verifyYAxisDisplay(preparedYAxes);

        //Create the lines/background for the objectives
        this.objectiveLines = new Array();
        this.objectiveBoxes = new Array();

        const objectiveData = this.data?.ObjectiveData;

        const direction = this.data.Metric.DefaultDirection;

        for (let i = 0; i < objectiveData?.poColors?.length; i++) {
            const minIndex = objectiveData.poDetails.length - i;
            const maxIndex = objectiveData.poDetails.length - (i + 1);
            const colorIndex = direction === 1 ? i : objectiveData.poColors.length - (i + 1);
            const lastIndex = objectiveData.poColors[colorIndex].RgbaColor.lastIndexOf(',');
            const color = objectiveData.poColors[colorIndex].RgbaColor.substring(0, lastIndex) + ',.2)';
            this.objectiveBoxes.push(
                {
                    type: 'box',
                    yMin: i === 0 ? null : objectiveData.poDetails[minIndex].Value,
                    yMax: i === objectiveData.poDetails.length ? null : objectiveData.poDetails[maxIndex].Value,
                    backgroundColor: color,
                    borderWidth: 0,
                    drawTime: 'beforeDatasetsDraw'
                }
            )

            if (i < objectiveData.poDetails.length) {
                const lastIndex = objectiveData.poColors[colorIndex].RgbaColor.lastIndexOf(',');
                const color = objectiveData.poColors[colorIndex].RgbaColor.substring(0, lastIndex) + ',.15)';
                this.objectiveLines.push(
                    {
                        type: 'line',
                        yMin: objectiveData.poDetails[maxIndex].Value,
                        yMax: objectiveData.poDetails[maxIndex].Value,
                        borderColor: color,
                        borderWidth: 3,
                        drawTime: 'beforeDatasetsDraw'
                    });
            }
        }

        this.hasObjective = this.objectiveBoxes.length > 0;
        if (!this.hasObjective) this.showObjective = false;

        //Create the chart
        this.chartConfig = {
            type: "line",
            data: {
                datasets: preppedDatasets,
                labels: xAxisLabels,
            },
            options: {
                maintainAspectRatio: false, //Disable to keep responsiveness, but stop pushing data off the bottom of the module pane
                responsive: true,
                spanGaps: false, //Tells Chart.js to not draw lines between points when data is NaN/null.

                /* --- Object Based Options --- */
                animation: {
                    duration: !this.firstAnimateComplete
                        ? 1000
                        : 0 /* If the first animation has not run, use the Chart.js default 1000, else set it to 0. */,
                },
                elements: {
                    line: {
                        tension: 0.3,
                    },
                },
                layout: {
                    padding: this.chartPadding,
                },
                plugins: {
                    annotation: {
                        annotations: this.showObjective ? [...this.objectiveLines, ...this.objectiveBoxes] : []
                    },
                    legend: {
                        onClick: null, // disable the hiding of legend item since there is only ever one
                        position: this.chartLegendPosition,
                        labels: {
                            boxWidth: 16,
                        },
                    },
                    title: {
                        display: false,
                        text: "",
                    },
                    tooltip: {
                        intersect: this.config.isCxOne ? true : false,
                        mode: this.config.isCxOne ? "point" : "index",
                        position: "nearest",
                        padding: this.config.isCxOne ? 12 : 6,
                        usePointStyle: true,
                        boxPadding: this.config.isCxOne ? 3 : 1,
                        boxWidth: this.config.isCxOne ? 11 : 18,
                        boxHeight: this.config.isCxOne ? 11 : 18,
                        borderColor: this.config.isCxOne
                            ? "#D0D2D3"
                            : "#353a40",
                        titleFont: {
                            size: this.config.isCxOne ? 12 : 16,
                            weight: this.config.isCxOne ? "normal" : "bold",
                        },
                        titleColor: this.config.isCxOne ? "#8c95a5" : "#fff",
                        bodyColor: this.config.isCxOne ? "#353a40" : "#fff",
                        footerColor: "#353a40",
                        backgroundColor: this.config.isCxOne
                            ? "#FFF"
                            : "rgba(0, 0, 0, 0.8)",
                        bodyFont: {
                            size: 14,
                        },
                        footerFont: {
                            size: 14,
                            weight: "bold",
                        },
                        borderWidth: this.config.isCxOne ? 1 : 0,
                        cornerRadius: this.config.isCxOne ? 0 : 6,
                        callbacks: {
                            // tooltipItem is an array of all Y-values for the given X-value/position being hovered over.
                            // tooltipItem should always have something at index [0] regardless of how many are in the series.
                            // So, get the series-data index of tooltipItem[0], which is used to pull the right x-value label.
                            title: (tooltipItemArray) =>
                                this.config.isCxOne
                                    ? this.datePipe.transform(
                                        new Date(),
                                        "MMM dd, yyyy"
                                    ) +
                                    ", " +
                                    tooltipItemArray[0].label
                                    : tooltipItemArray[0].label,
                            label: (tooltipItem) => {
                                const pmLabel =
                                    tooltipItem.dataset.label +
                                    ": " +
                                    (tooltipItem.dataset as any).displayValues[
                                        tooltipItem.dataIndex
                                    ];
                                return this.config.isCxOne
                                    ? tooltipItem.dataset.label
                                    : pmLabel;
                            },
                            footer: (tooltipItem) =>
                                this.config.isCxOne
                                    ? "     " +
                                    (tooltipItem[0].dataset as any)
                                        .displayValues[
                                            tooltipItem[0].dataIndex
                                        ]
                                    : "",
                            labelColor: (tooltipItem) => ({
                                borderColor: tooltipItem.dataset.borderColor,
                                backgroundColor:
                                    tooltipItem.dataset.backgroundColor,
                                borderWidth: this.config.isCxOne ? 0 : 0.5,
                            }),
                            labelPointStyle: (context) => ({
                                pointStyle: this.config.isCxOne
                                    ? "circle"
                                    : "rect",
                                rotation: 0,
                            }),
                        },
                    },
                },
                point: {
                    hitRadius: 4,
                    hoverRadius: 4,
                    pointRadius: 0,
                },
                scales: {
                    x: {
                        display: true,
                        distribution: "series",
                        scaleLabel: {
                            display: false,
                        },
                        ticks: {
                            autoSkip: true,
                            source: "labels",
                            maxRotation: this.chartMaximumRotation,
                        },
                    },
                    ...preparedYAxes,
                },
            },
            plugins: [this.backgroundcolor]
        } as ChartConfiguration;
        this.firstAnimateComplete = true;
    }

    verifyYAxisDisplay(preparedYAxes) {
        for (const key in preparedYAxes) {
            if (this.chartAxisShowStates[key] === 0) {
                preparedYAxes[key].display = false;
            }
        }
    }

    async setLegendPosition(position) {
        //Set position string
        this.chartLegendPosition = position;
        this.chartState.legendPosition = position;

        //Set padding values
        switch (position) {
            case ('top'): //Default view, Chart.js defaults all padding values to 0.
                this.chartPadding = {
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0,
                };
                break;
            case ('bottom'): //Extra top space. Bottom is padded by the legend, so no extra bottom padding.
                this.chartPadding = {
                    top: 16,
                    bottom: 0,
                    left: 0,
                    right: 0,
                };
                break;
            case ('left'): //Extra top and bottom space
                this.chartPadding = {
                    top: 16,
                    bottom: 8,
                    left: 0,
                    right: 0,
                };
                break;
            case ('right'): //Extra top and bottom space
                this.chartPadding = {
                    top: 16,
                    bottom: 8,
                    left: 0,
                    right: 0,
                };
                break;
            default: //Same as 'top'
                this.chartPadding = {
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0,
                };
                break;
        }

        // Apply changes
        // Moving the legend requires the chart to be completely recreated instead of just updated.
        // This method (using a variable rather than direct manipulation) also allows the padding values to be retained if the chart is recreated in any way.
        this.createChart();
        if (await this.moduleManager.saveState("chartState")) {
            this.moduleManager.requestPublishNeeded();
        }
    }

    async handleShowObjective() {
        if (!this.showObjective) {
            this.chartObject.chart.options.plugins.annotation.annotations = [];
            this.chartObject.chart.update();
        }
        else {
            this.chartObject.chart.options.plugins.annotation.annotations = [...this.objectiveLines, ...this.objectiveBoxes];
            this.chartObject.chart.update();
        }

        this.chartState.showObjective = this.showObjective;

        if (await this.moduleManager.saveState("chartState")) {
            this.moduleManager.requestPublishNeeded();
        }
    }

    //Updates the suffix after the module name.
    updateModuleNameSuffix() {
        if (this.data?.GraphItem) {
            const metricName = this.data.GraphItem.MetricCustomName ?
                `${this.data.GraphItem.MetricCustomName}*` : this.data.GraphItem.MetricName;
            const suffix = " - " + metricName;
            this.moduleManager.setModuleNameSuffix(suffix);
        } else {
            this.moduleManager.removeModuleNameSuffix();
        }
    }

    exportPNG() {
        // toBase64Image() returns a png data url of the image on the canvas. (Sourced: https://www.chartjs.org/docs/latest/developers/api.html)

        // name of the file
        const date = (new Date()).toString('yyyy_MM_dd_HH_mm_ss');
        const fileName = this.cvLocalizationService.getCachedText("Module_Names", "Trending", "Trending")
            .replace(/\s+/g, '-').toLowerCase() + '_widget_(' + date + ').png';

        // create fake link
        const link = document.createElement('a');
        link.setAttribute('download', fileName);
        link.setAttribute('href', this.chartObject.chart.toBase64Image());

        // start download
        link.click();

        // cleanup
        URL.revokeObjectURL(link.href);
        link.remove();
    }

}

